import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { OrderRefundApi } from "../../api/OrderRefundApi";
import type { CashDrawerChangeFetchOptions } from "../../generated/dto/CashDrawerChangeFetchOptions";
import type { CashDrawerChangeStaff } from "../../generated/dto/CashDrawerChangeStaff";
import type { LineItemRefundFetchOptions } from "../../generated/dto/LineItemRefundFetchOptions";
import type { LineItemRefundStaff } from "../../generated/dto/LineItemRefundStaff";
import type { OrderFetchOptions } from "../../generated/dto/OrderFetchOptions";
import type { OrderPaymentFetchOptions } from "../../generated/dto/OrderPaymentFetchOptions";
import type { OrderPaymentStaff } from "../../generated/dto/OrderPaymentStaff";
import type { OrderRef } from "../../generated/dto/OrderRef";
import type { OrderRefundFetchOptions } from "../../generated/dto/OrderRefundFetchOptions";
import type { OrderRefundPreview } from "../../generated/dto/OrderRefundPreview";
import type { OrderRefundPreviewRequest } from "../../generated/dto/OrderRefundPreviewRequest";
import type { OrderRefundReconciliation } from "../../generated/dto/OrderRefundReconciliation";
import type { OrderRefundRequest } from "../../generated/dto/OrderRefundRequest";
import type { OrderRefundResult } from "../../generated/dto/OrderRefundResult";
import type { OrderRefundStaff } from "../../generated/dto/OrderRefundStaff";
import type { OrderStaff } from "../../generated/dto/OrderStaff";
import type { PaymentRefundFetchOptions } from "../../generated/dto/PaymentRefundFetchOptions";
import type { PaymentRefundStaff } from "../../generated/dto/PaymentRefundStaff";

export class OrderRefundApiClient implements OrderRefundApi {
  constructor(private readonly client = useClient()) {}

  async getOrderRefundStaff(id: number, options?: OrderRefundFetchOptions): Promise<OrderRefundStaff> {
    const { data } = await this.client.get<unknown>(`/orderrefund/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.OrderRefundFetchOptions") });
    return deserializeCbiDto<OrderRefundStaff>(data, "generated.dto.OrderRefundStaff");
  }

  async getPaymentRefundStaff(id: number, options?: PaymentRefundFetchOptions): Promise<PaymentRefundStaff> {
    const { data } = await this.client.get<unknown>(`/paymentrefund/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.PaymentRefundFetchOptions") });
    return deserializeCbiDto<PaymentRefundStaff>(data, "generated.dto.PaymentRefundStaff");
  }

  async getOrderRefundStaffOrderId(id: number, options?: OrderFetchOptions): Promise<OrderStaff> {
    const { data } = await this.client.get<unknown>(`/orderrefund/staff/${id}/orderid`, { params: serializeCbiDto(options, "generated.dto.OrderFetchOptions") });
    return deserializeCbiDto<OrderStaff>(data, "generated.dto.OrderStaff");
  }

  async getOrderRefundStaffLineItemRefunds(id: number, options?: LineItemRefundFetchOptions): Promise<Array<LineItemRefundStaff>> {
    const { data } = await this.client.get<unknown>(`/orderrefund/staff/${id}/lineitemrefunds`, { params: serializeCbiDto(options, "generated.dto.LineItemRefundFetchOptions") });
    return deserializeCbiDto<Array<LineItemRefundStaff>>(data, "generated.dto.LineItemRefundStaff");
  }

  async getOrderRefundStaffPaymentRefunds(id: number, options?: PaymentRefundFetchOptions): Promise<Array<PaymentRefundStaff>> {
    const { data } = await this.client.get<unknown>(`/orderrefund/staff/${id}/paymentrefunds`, { params: serializeCbiDto(options, "generated.dto.PaymentRefundFetchOptions") });
    return deserializeCbiDto<Array<PaymentRefundStaff>>(data, "generated.dto.PaymentRefundStaff");
  }

  async getPaymentRefundStaffCashDrawerChange(id: number, options?: CashDrawerChangeFetchOptions): Promise<CashDrawerChangeStaff | null> {
    const { data } = await this.client.get<unknown>(`/paymentrefund/staff/${id}/cashdrawerchange`, { params: serializeCbiDto(options, "generated.dto.CashDrawerChangeFetchOptions") });
    return deserializeCbiDto<CashDrawerChangeStaff | null>(data, "generated.dto.CashDrawerChangeStaff");
  }

  async getPaymentRefundStaffOrderRefundId(id: number, options?: OrderRefundFetchOptions): Promise<OrderRefundStaff> {
    const { data } = await this.client.get<unknown>(`/paymentrefund/staff/${id}/orderrefundid`, { params: serializeCbiDto(options, "generated.dto.OrderRefundFetchOptions") });
    return deserializeCbiDto<OrderRefundStaff>(data, "generated.dto.OrderRefundStaff");
  }

  async getPaymentRefundStaffPaymentId(id: number, options?: OrderPaymentFetchOptions): Promise<OrderPaymentStaff> {
    const { data } = await this.client.get<unknown>(`/paymentrefund/staff/${id}/paymentid`, { params: serializeCbiDto(options, "generated.dto.OrderPaymentFetchOptions") });
    return deserializeCbiDto<OrderPaymentStaff>(data, "generated.dto.OrderPaymentStaff");
  }

  async markOrderRefundedStaff(order: OrderRef): Promise<OrderRefundReconciliation> {
    const { data } = await this.client.post<unknown>(`/orderrefund/staff/markorderrefunded`, serializeCbiDto(order, "generated.dto.OrderRef"));
    return deserializeCbiDto<OrderRefundReconciliation>(data, "generated.dto.OrderRefundReconciliation");
  }

  async previewOrderRefundStaff(request: OrderRefundPreviewRequest): Promise<OrderRefundPreview> {
    const { data } = await this.client.post<unknown>(`/orderrefund/staff/previeworderrefund`, serializeCbiDto(request, "generated.dto.OrderRefundPreviewRequest"));
    return deserializeCbiDto<OrderRefundPreview>(data, "generated.dto.OrderRefundPreview");
  }

  async refundOrderStaff(request: OrderRefundRequest): Promise<OrderRefundResult> {
    const { data } = await this.client.post<unknown>(`/orderrefund/staff/refundorder`, serializeCbiDto(request, "generated.dto.OrderRefundRequest"));
    return deserializeCbiDto<OrderRefundResult>(data, "generated.dto.OrderRefundResult");
  }
}

export const useOrderRefundApi = (): OrderRefundApi => new OrderRefundApiClient();
